const { CONSENT_KEY, DENIED, readConsent, serializeConsent, safePath, safeReferrer,
  safeCampaign, serverContainerUrl, safeLocale, acknowledgedLead, safeLead, consentedFbp, metaReferrerAllowed } = require("./tracking-policy");
const { isThankYouPage } = require("./analytics-privacy");
const { performanceEvent } = require("./telemetry-privacy");

const GA_ID = "G-L9QNV4PFVF";
const META_ID = "1920076291714578";
const SERVER_SNAPSHOT = Object.freeze({ ready: false, consent: null });

function createTrackingClient(win, doc, options = {}) {
  const gaId = options.gaId || GA_ID;
  const metaId = options.metaId || META_ID;
  const endpoint = serverContainerUrl(options.serverUrl, options.approvedServerHost);
  const gaEnabled = /^G-[A-Z0-9]+$/.test(gaId) && endpoint !== null;
  const metaEnabled = options.metaEnabled === true && /^\d+$/.test(metaId);
  const listeners = new Set();
  const delivered = new Set();
  let snapshot = SERVER_SNAPSHOT;
  let initialized = false, gaStarted = false, metaStarted = false, pageAllowed = false;
  let lastGooglePage = "", lastMetaPage = "", locale = "en";
  let attribution = {};

  function emit() { listeners.forEach(listener => listener()); }
  function consent() { return snapshot.consent || DENIED; }
  function currentPath() { return safePath(win.location.pathname); }
  function safeMetaLocation() {
    return pageAllowed && metaReferrerAllowed(doc.referrer) && !win.location.search && !win.location.hash &&
      currentPath() === win.location.pathname && !isThankYouPage(currentPath());
  }
  function googleState(choices) {
    // Marketing consent controls Meta independently. Google advertising,
    // enhanced conversions and personalization stay disabled in this release.
    return { analytics_storage: choices.analytics ? "granted" : "denied",
      ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" };
  }
  function parameters(path = currentPath()) {
    return { page_path: safePath(path), page_location: `${win.location.origin}${safePath(path)}`,
      page_referrer: safeReferrer(doc.referrer), page_title: "DigiFist", language: locale,
      marketing_consent: consent().marketing ? "granted" : "denied",
      ...attribution };
  }
  function loadScript(id, src) {
    if (doc.getElementById(id)) return;
    const script = doc.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    // Prevent the script request's Referer from carrying a same-origin path.
    script.referrerPolicy = "origin";
    doc.head.appendChild(script);
  }
  function clearCookies(choices) {
    const names = (doc.cookie || "").split(";").map(value => value.split("=")[0].trim());
    const hosts = ["", win.location.hostname, `.${win.location.hostname}`];
    for (const base of ["digifist.com", "digifist.com.tr"]) {
      if (win.location.hostname === base || win.location.hostname.endsWith(`.${base}`)) hosts.push(base, `.${base}`);
    }
    const paths = new Set(["/"]);
    const segments = win.location.pathname.split("/").filter(Boolean);
    while (segments.length) { paths.add(`/${segments.join("/")}`); segments.pop(); }
    for (const name of names) {
      const analyticsCookie = /^_(?:ga(?:_|$)|gid$|gat(?:_|$))/.test(name);
      const marketingCookie = /^_(?:fbp$|fbc$|gcl_)/.test(name);
      if ((!choices.analytics && analyticsCookie) || (!choices.marketing && marketingCookie)) {
        for (const domain of new Set(hosts)) for (const path of paths) {
          doc.cookie = `${name}=; Max-Age=0; Path=${path}${domain ? `; Domain=${domain}` : ""}; SameSite=Lax; Secure`;
        }
      }
    }
  }
  function prepareGoogle() {
    if (gaStarted || !gaEnabled || !pageAllowed || !consent().analytics || isThankYouPage(currentPath())) return;
    gaStarted = true;
    win.dataLayer = win.dataLayer || [];
    win.gtag = function () { win.dataLayer.push(arguments); };
    win.gtag("consent", "default", googleState(DENIED));
    win.gtag("set", "ads_data_redaction", true);
    win.gtag("set", "url_passthrough", false);
    win.gtag("consent", "update", googleState(consent()));
    win.gtag("js", new Date());
    win.gtag("config", gaId, { ...parameters(), send_page_view: false,
      allow_google_signals: false, allow_ad_personalization_signals: false,
      ...(endpoint ? { server_container_url: endpoint } : {}) });
    loadScript("digifist-google-tag", `https://www.googletagmanager.com/gtag/js?id=${gaId}`);
  }
  function prepareMeta() {
    if (metaStarted || !metaEnabled || !consent().marketing || !safeMetaLocation()) return;
    metaStarted = true;
    const fbq = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    win.fbq = fbq;
    win._fbq = fbq;
    fbq("consent", "revoke");
    fbq("set", "autoConfig", false, metaId);
    fbq("init", metaId); // No advanced-matching or submitted-field object.
    fbq("consent", "grant");
    loadScript("digifist-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  }
  function pageview(url, nextLocale = locale, allowed = true) {
    pageAllowed = allowed === true;
    locale = safeLocale(nextLocale);
    const path = safePath(url || win.location.pathname);
    const key = `${locale}:${path}`;
    const allowedGoogle = pageAllowed && consent().analytics && gaEnabled && !isThankYouPage(path);
    win[`ga-disable-${gaId}`] = !allowedGoogle;
    if (allowedGoogle) {
      prepareGoogle();
      if (gaStarted && lastGooglePage !== key) {
        win.gtag("set", parameters(path));
        win.gtag("event", "page_view", { ...parameters(path), send_to: gaId });
        lastGooglePage = key;
      }
    } else lastGooglePage = "";
    const allowedMeta = consent().marketing && metaEnabled && safeMetaLocation();
    if (allowedMeta) {
      prepareMeta();
      if (metaStarted) {
        win.fbq("consent", "grant");
        if (lastMetaPage !== key) { win.fbq("trackSingle", metaId, "PageView"); lastMetaPage = key; }
      }
    } else {
      lastMetaPage = "";
      if (metaStarted) win.fbq("consent", "revoke");
    }
  }
  function pauseForNavigation() {
    pageAllowed = false;
    win[`ga-disable-${gaId}`] = true;
    if (metaStarted) win.fbq("consent", "revoke");
  }
  function applyChoices(choices, persist) {
    const next = { analytics: choices?.analytics === true, marketing: choices?.marketing === true };
    const previous = consent();
    const revoked = (previous.analytics && !next.analytics) ||
      (previous.marketing && !next.marketing && metaStarted);
    if (persist) { try { win.localStorage.setItem(CONSENT_KEY, serializeConsent(next)); } catch { /* Session-only choice. */ } }
    snapshot = { ready: true, consent: next };
    if (!next.analytics) win[`ga-disable-${gaId}`] = true;
    if (gaStarted) win.gtag("consent", "update", googleState(next));
    if (metaStarted && !next.marketing) win.fbq("consent", "revoke");
    clearCookies(next);
    emit();
    if (revoked) {
      // Removing a script does not stop its timers/listeners. Reload into the
      // saved choice, with no tracking bootstrap for the withdrawn category.
      for (const id of ["digifist-google-tag", "digifist-meta-pixel"]) doc.getElementById(id)?.remove();
      win.location.reload();
      return;
    }
    pageview(win.location.pathname, locale, pageAllowed);
  }
  function initialize() {
    if (initialized) return;
    initialized = true;
    let stored = null;
    try { stored = readConsent(win.localStorage.getItem(CONSENT_KEY)); } catch { /* Default denied. */ }
    // Only a small fixed vocabulary is accepted; no names, email, company,
    // campaign free text or click IDs are copied from query parameters.
    attribution = safeCampaign(win.location.href);
    snapshot = { ready: true, consent: stored };
    clearCookies(stored || DENIED);
    win.addEventListener("storage", event => {
      if (event.key === CONSENT_KEY || event.key === null) applyChoices(readConsent(event.newValue) || DENIED, false);
    });
    emit();
  }
  function lead(response, metadata) {
    if (!acknowledgedLead(response)) return false;
    const payload = safeLead(metadata);
    if (!payload || delivered.has(payload.event_id)) return false;
    // Denied submissions are not queued and are never replayed after opt-in.
    const google = pageAllowed && consent().analytics && gaEnabled && gaStarted && !isThankYouPage(currentPath());
    const meta = consent().marketing && metaEnabled && metaStarted && safeMetaLocation();
    delivered.add(payload.event_id);
    if (delivered.size > 200) delivered.delete(delivered.values().next().value);
    if (!google && !meta) return false;
    // This pseudonymous Meta cookie is only a transport field for the reviewed
    // server destination. Its GA4 forwarding tag must strip it. Never include
    // it with direct Google collection or analytics-only consent.
    const fbp = endpoint && google && meta ? consentedFbp(doc.cookie) : undefined;
    if (google) win.gtag("event", "generate_lead", { ...parameters(), ...payload, send_to: gaId,
      ...(fbp ? { consented_fbp: fbp } : {}) });
    if (meta) win.fbq("trackSingle", metaId, "Lead",
      { lead_type: payload.lead_type, form_path: payload.form_path, locale: payload.locale }, { eventID: payload.event_id });
    return true;
  }
  return {
    initialize, pageview, pauseForNavigation, lead,
    performance: (event, route) => performanceEvent(event, { allowed: pageAllowed && consent().analytics,
      origin: win.location.origin, path: win.location.pathname, route }),
    saveConsent: choices => applyChoices(choices, true),
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); initialize(); return () => listeners.delete(listener); },
  };
}

let browserClient;
function getBrowserClient() {
  if (typeof window === "undefined") return null;
  if (!browserClient) browserClient = createTrackingClient(window, document, {
    gaId: GA_ID, metaId: META_ID,
    metaEnabled: process.env.NEXT_PUBLIC_META_PIXEL_ENABLED === "true",
    serverUrl: process.env.NEXT_PUBLIC_SERVER_CONTAINER_URL || "",
    approvedServerHost: process.env.NEXT_PUBLIC_SERVER_CONTAINER_APPROVED_HOST || "",
  });
  return browserClient;
}
function createLeadEventId() {
  if (typeof window === "undefined" || !window.crypto?.randomUUID) return null;
  return window.crypto.randomUUID();
}
function trackLead(response, metadata) {
  try { return getBrowserClient()?.lead(response, metadata) || false; } catch { return false; }
}
function subscribeTracking(listener) { return getBrowserClient()?.subscribe(listener) || (() => {}); }
function getTrackingSnapshot() { return getBrowserClient()?.getSnapshot() || SERVER_SNAPSHOT; }
function getServerSnapshot() { return SERVER_SNAPSHOT; }
function saveConsent(choices) { getBrowserClient()?.saveConsent(choices); }
function trackPage(url, locale, allowed) { getBrowserClient()?.pageview(url, locale, allowed); }
function pauseTracking() { getBrowserClient()?.pauseForNavigation(); }
function sanitizePerformanceEvent(event, route) { return getBrowserClient()?.performance(event, route) || null; }

module.exports = { GA_ID, META_ID, createTrackingClient, createLeadEventId, trackLead,
  subscribeTracking, getTrackingSnapshot, getServerSnapshot, saveConsent, trackPage, pauseTracking, sanitizePerformanceEvent };
